import { useEffect, useRef } from 'react';
import { useSupervisor } from '@/hooks/useSupervisor';
import { Order } from '@/types/supervisor';
import type { UploadSuccessPayload } from '@/components/order/ImageUploader';
import { ImageUploader } from '../order/ImageUploader';
import { UploadedImagesGrid } from '../order/UploadedImagesGrid';
import SizeStepPanel from '@/components/order/SizeStepPanel';
import PaperStepPanel from '@/components/order/PaperStepPanel';
import PrinterStepPanel from '@/components/order/PrinterStepPanel';
import DefaultCanvas from './DefaultCanvas';
import FrameStepPlaceholder from '@/components/order/FrameStepPlaceholder';
import ShippingAddressDialog from '../order/ShippingAddressDialog';
import { Login } from '../auth/Login';
import { store } from '@/app/store';
import {
  updateOrderItem,
  recalculateOrderItemPrice,
  persistDirtyOrderItems,
  setShippingAddress,
  setFulfillmentMode,
} from '@/features/order/orderSlice';
import type { PrintSizeValue } from '@/components/order/utils/printSizeForm';
import OrderSummaryContainer from '../order/OrderSummaryContainer';
import { OrderListCanvas } from '../order/OrderListCanvas';
import { analytics } from '@/services/analytics';
import { logging } from '@/services/logging';
import { sanitizeOrderItems } from '@/features/order/utils/canonicalOrder';
import type { OrderItemStatus } from '@/types/orderStatus';

// A mapping from state machine state names to the components that render them.
const componentMap: Record<string, React.ComponentType<any>> = {
  'login': Login,
  'screen.upload': ImageUploader,
  'screen.size': SizeStepPanel,
  'screen.printer': PrinterStepPanel,
  'screen.paper': PaperStepPanel,
  'screen.frame': FrameStepPlaceholder,
  'screen.address': ShippingAddressDialog,
  'screen.review': UploadedImagesGrid,
  'screen.uploadedImages': UploadedImagesGrid,
  'screen.payment': OrderSummaryContainer,
  'screen.login': Login,
  'screen.done': OrderListCanvas,
};

const STATUS_PRIORITY: Record<OrderItemStatus, number> = {
  NEW: 0,
  IN_ORDER: 1,
  FIRST_ORDER: 2,
  SECOND_ORDER: 3,
  PAID: 4,
  REUPLOAD_IMAGE: 5,
  REUPLOADED: 6,
  READY_TO_PRINT: 7,
  PRINTED: 8,
  IN_DELIVERY: 9,
  DELIVERED: 10,
  RETURNED: 11,
  CANCELLED: 12,
};

const normalizeStatus = (value: unknown): OrderItemStatus | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const upper = value.trim().toUpperCase();
  if (upper === 'CONFIGURING' || upper === 'CONFIGURED') {
    return 'NEW';
  }
  if (upper in STATUS_PRIORITY) {
    return upper as OrderItemStatus;
  }
  return undefined;
};

const chooseStatus = (
  current?: unknown,
  candidate?: unknown,
): OrderItemStatus | undefined => {
  const resolvedCurrent = normalizeStatus(current);
  const resolvedCandidate = normalizeStatus(candidate);
  if (!resolvedCurrent) {
    return resolvedCandidate;
  }
  if (!resolvedCandidate) {
    return resolvedCurrent;
  }
  return STATUS_PRIORITY[resolvedCandidate] > STATUS_PRIORITY[resolvedCurrent]
    ? resolvedCandidate
    : resolvedCurrent;
};

interface UploadProps {
  order: any;
  onUploadError?: (message: string) => void;
}

interface DynamicComponentProps {
  uiPayload: {
    name: string;
    props: UploadProps;
  };
}

export const DynamicComponent = ({ uiPayload }: DynamicComponentProps) => {
  const { send: sendToSupervisor } = useSupervisor();
  const { name, props } = uiPayload;
  const initialItems = Array.isArray(props.order?.items) ? props.order.items : [];
  const itemsRef = useRef<Order['items']>(sanitizeOrderItems(initialItems));

  const ComponentToRender = componentMap[name] || DefaultCanvas;

  // Callbacks that will be passed to the child components.
  // These now send events to the state machine instead of dispatching Redux actions.

  useEffect(() => {
    if (Array.isArray(props.order?.items)) {
      itemsRef.current = sanitizeOrderItems(props.order.items);
    }
  }, [props.order?.items]);

  const onUploadSuccess = ({ orderItem, file }: UploadSuccessPayload) => {
    const orderItemId = orderItem.id;
    if (!orderItemId) {
      return;
    }

    const existingItems = Array.isArray(itemsRef.current) ? itemsRef.current : [];
    const alreadyPresent = existingItems.some((item) => item.id === orderItemId);

    if (alreadyPresent) {
      return;
    }

    const nextItems = [
      ...existingItems,
      {
        id: orderItemId,
        uploadedFileId: orderItem.uploadedFileId ?? undefined,
        status: orderItem.status,
        orderId: orderItem.orderId ?? undefined,
        originalWidthPx: file.originalWidthPx,
        originalHeightPx: file.originalHeightPx,
        originalWidthIn: file.widthIn,
        originalHeightIn: file.heightIn,
        dpiX: file.dpiX,
        dpiY: file.dpiY,
        widthIn: orderItem.widthIn ?? undefined,
        heightIn: orderItem.heightIn ?? undefined,
        price: orderItem.priceUsd != null
          ? {
              value: orderItem.priceUsd,
              currency: 'USD',
            }
          : undefined,
        pricingStatus: orderItem.priceUsd != null ? 'succeeded' : 'idle',
      },
    ];

    const sanitizedItems = sanitizeOrderItems(nextItems);
    itemsRef.current = sanitizedItems;

    sendToSupervisor({
      type: 'ORDER.APPLY_PATCH',
      patch: { items: sanitizedItems },
    });

    sendToSupervisor({ type: 'UI.IMAGES_UPLOADED' });
  };

  const onUploadError = (message: string) => {
    if (typeof props.onUploadError === 'function') {
      props.onUploadError(message);
      return;
    }
    logging.warn('Image upload error', { message });
  };

  const onUploadSummary = (summaryText: string) => {
    sendToSupervisor({
      type: 'UI.ANNOUNCE_UPLOAD_SUMMARY',
      summaryText,
    });
    sendToSupervisor({ type: 'UI.IMAGES_UPLOADED' });
  };

  const onUpdatePrintSize = (itemId: string, size: PrintSizeValue) => {
    const existingItems = Array.isArray(itemsRef.current) ? itemsRef.current : [];
    const storeItems = store.getState().order.items ?? [];
    const storeItemsById = new Map(storeItems.map((storeItem) => [storeItem.id, storeItem] as const));

    const nextItems = existingItems.map((item) => {
      const storeItem = item?.id ? storeItemsById.get(item.id) : undefined;
      const merged = {
        ...item,
        ...(storeItem ?? {}),
      };
      if (merged.uploadedFileId == null) {
        delete merged.uploadedFileId;
      }
      if (item.id === itemId) {
        merged.widthIn = size.widthIn;
        merged.heightIn = size.heightIn;
        if (typeof size.marginIn === 'number' && size.marginIn > 0) {
          merged.marginIn = size.marginIn;
        } else {
          delete merged.marginIn;
        }
        if (typeof size.galleryWrapIn === 'number' && size.galleryWrapIn > 0) {
          merged.galleryWrapIn = size.galleryWrapIn;
          merged.galleryWrapMirrorEdges = size.galleryWrapMirrorEdges === true;
        } else {
          delete merged.galleryWrapIn;
          delete merged.galleryWrapMirrorEdges;
        }
      }
      const preservedStatus = chooseStatus(item.status, storeItem?.status);
      if (preservedStatus) {
        merged.status = preservedStatus;
      } else {
        delete merged.status;
      }
      return merged;
    });

    const sanitizedItems = sanitizeOrderItems(nextItems);
    itemsRef.current = sanitizedItems;
    sendToSupervisor({ type: 'ORDER.APPLY_PATCH', patch: { items: sanitizedItems } });
    const preservedStatus = chooseStatus(
      existingItems.find((item) => item.id === itemId)?.status,
      storeItemsById.get(itemId)?.status,
    );
    const updatePayload: {
      widthIn: number;
      heightIn: number;
      marginIn?: number | null;
      galleryWrapIn?: number | null;
      galleryWrapMirrorEdges?: boolean | null;
      status?: OrderItemStatus;
    } = {
      widthIn: size.widthIn,
      heightIn: size.heightIn,
    };
    if (typeof size.marginIn === 'number' && size.marginIn > 0) {
      updatePayload.marginIn = size.marginIn;
    } else {
      updatePayload.marginIn = null;
    }
    if (typeof size.galleryWrapIn === 'number' && size.galleryWrapIn > 0) {
      updatePayload.galleryWrapIn = size.galleryWrapIn;
      updatePayload.galleryWrapMirrorEdges = size.galleryWrapMirrorEdges === true;
    } else {
      updatePayload.galleryWrapIn = null;
      updatePayload.galleryWrapMirrorEdges = null;
    }

    if (preservedStatus) {
      updatePayload.status = preservedStatus;
    }

    store.dispatch(updateOrderItem({ orderItemId: itemId, data: updatePayload }));
    void store.dispatch(recalculateOrderItemPrice({ orderItemId: itemId }));
    void store.dispatch(persistDirtyOrderItems({ itemIds: [itemId] }));
  };

  const onUpdatePaperType = (itemId: string, paperTypeId: string) => {
    const existingItems = Array.isArray(itemsRef.current) ? itemsRef.current : [];
    const storeItems = store.getState().order.items ?? [];
    const storeItemsById = new Map(storeItems.map((storeItem) => [storeItem.id, storeItem] as const));

    const nextItems = existingItems.map((item) => {
      const storeItem = item?.id ? storeItemsById.get(item.id) : undefined;
      const merged = {
        ...item,
        ...(storeItem ?? {}),
      };
      if (merged.uploadedFileId == null) {
        delete merged.uploadedFileId;
      }

      if (item.id === itemId) {
        merged.paperTypeId = paperTypeId;
      }

      const preservedStatus = chooseStatus(item.status, storeItem?.status);
      if (preservedStatus) {
        merged.status = preservedStatus;
      } else {
        delete merged.status;
      }

      return merged;
    });

    const sanitizedItems = sanitizeOrderItems(nextItems);
    itemsRef.current = sanitizedItems;
    sendToSupervisor({ type: 'ORDER.APPLY_PATCH', patch: { items: sanitizedItems } });
  };

  const onUpdatePrinter = (itemId: string, printerId: string) => {
    const existingItems = Array.isArray(itemsRef.current) ? itemsRef.current : [];
    const storeItems = store.getState().order.items ?? [];
    const storeItemsById = new Map(storeItems.map((storeItem) => [storeItem.id, storeItem] as const));

    const nextItems = existingItems.map((item) => {
      const storeItem = item?.id ? storeItemsById.get(item.id) : undefined;
      const merged = {
        ...item,
        ...(storeItem ?? {}),
      };
      if (merged.uploadedFileId == null) {
        delete merged.uploadedFileId;
      }

      if (item.id === itemId) {
        merged.printerId = printerId;
      }

      const preservedStatus = chooseStatus(item.status, storeItem?.status);
      if (preservedStatus) {
        merged.status = preservedStatus;
      } else {
        delete merged.status;
      }

      return merged;
    });

    const sanitizedItems = sanitizeOrderItems(nextItems);
    itemsRef.current = sanitizedItems;
    sendToSupervisor({ type: 'ORDER.APPLY_PATCH', patch: { items: sanitizedItems } });
  };

  const onAddressConfirm = (address: any) => {
    analytics.track('button: ui.address_confirm_click', {
      step: 'address',
      mode: 'SHIPPING',
    });
    store.dispatch(setShippingAddress(address));
    store.dispatch(setFulfillmentMode('SHIPPING'));

    const latestItems = sanitizeOrderItems(store.getState().order.items ?? []);
    itemsRef.current = latestItems;

    sendToSupervisor({
      type: 'ORDER.APPLY_PATCH',
      patch: {
        items: latestItems,
        shippingAddress: address,
        fulfillmentMode: 'SHIPPING',
      },
    });
    sendToSupervisor({ type: 'UI.GOTO_STEP', step: 'payment' });
  };

  const onPickupConfirm = (address: any) => {
    analytics.track('button: ui.address_confirm_click', {
      step: 'address',
      mode: 'PICKUP',
    });
    store.dispatch(setShippingAddress(address));
    store.dispatch(setFulfillmentMode('PICKUP'));

    const latestItems = sanitizeOrderItems(store.getState().order.items ?? []);
    itemsRef.current = latestItems;

    sendToSupervisor({
      type: 'ORDER.APPLY_PATCH',
      patch: {
        items: latestItems,
        shippingAddress: address,
        fulfillmentMode: 'PICKUP',
      },
    });
    sendToSupervisor({ type: 'UI.GOTO_STEP', step: 'payment' });
  };

  const handleAddressCancel = () => {
    sendToSupervisor({ type: 'UI.GOTO_STEP', step: 'review' });
  };

  const onOrderItemSubmit = (itemData: any) => {
    // Assuming there's only one item for now for simplicity
    const sanitizedItems = sanitizeOrderItems([{ ...props.order.items?.[0], ...itemData }]);
    if (sanitizedItems.length > 0) {
      sendToSupervisor({ type: 'ORDER.APPLY_PATCH', patch: { items: sanitizedItems } });
    }
  };

  // Pass the correct callbacks to the component being rendered.
  const enhancedProps = {
    ...props,
    order: props.order,
    onUploadSuccess,
    onUploadError,
    onUploadSummary,
    onUpdatePrintSize,
    onConfirm: onAddressConfirm,
    onSubmit: onOrderItemSubmit,
    onUpdatePaperType,
    onUpdatePrinter,
  };

  if (name === 'screen.address') {
    Object.assign(enhancedProps, {
      open: true,
      initial: props.order?.shippingAddress,
      fulfillmentMode: props.order?.fulfillmentMode,
      onPickupConfirm,
      onCancel: handleAddressCancel,
    });
  }

  return <ComponentToRender {...enhancedProps} />;
};
